import { showNotification } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash-es';
import { apiClient, getEndpoints } from '../../../api/common';
import {
	NOTIFICATION_NAMESPACE,
	notificationUnreadCountQueryKeyFactory,
} from '../../../api/hooks/notification/constants';
import { invalidateNotificationListQuery } from '../../../api/hooks/notification/utils';
import { NOTIFICATION_MESSAGES } from '../constants';
import { inboxFeatureStore } from '../store';
import type {
	DeleteAllNotificationsParams,
	MarkAsReadParams,
	NotificationCountData,
	NotificationQueryData,
} from '../types';
import { updateNotificationReadStatus } from './notifications';

export function useMarkAsReadMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			all = false,
			eventFilter = '',
			status = true,
		}: MarkAsReadParams) => {
			const path = all ? 'read_all' : 'read';
			const url = getEndpoints(NOTIFICATION_NAMESPACE).byPath([path]);
			const params = all ? { events: eventFilter } : { id, status };

			const { data } = await apiClient.post(url, {}, { params });
			return data;
		},
		onMutate: async ({ id, status = true }) => {
			if (isNil(id)) return;

			// Cancel any outgoing refetches
			await queryClient.cancelQueries({
				queryKey: ['notification', 'notifications'],
			});

			// Snapshot the previous value
			const previousNotifications = queryClient.getQueriesData({
				queryKey: ['notification', 'notifications'],
			});

			const previousUnreadCount =
				queryClient.getQueryData<NotificationCountData>(
					notificationUnreadCountQueryKeyFactory.all()
				);

			// Optimistically update the notifications list
			queryClient.setQueriesData<NotificationQueryData>(
				{ queryKey: ['notification', 'notifications'] },
				(old) => updateNotificationReadStatus(old, id, status)
			);

			// Add to recently read if marking as read and not showing read notifications
			let addedToRecentlyRead = false;
			if (status && !inboxFeatureStore.showRead) {
				if (!inboxFeatureStore.isRecentlyRead(id)) {
					inboxFeatureStore.addToRecentlyRead(id);
					addedToRecentlyRead = true;
				}
			}

			return {
				previousNotifications,
				previousUnreadCount,
				addedToRecentlyRead,
				id,
			};
		},
		onError: (error, variables, context) => {
			// Rollback to previous state on error
			if (context?.previousNotifications) {
				context.previousNotifications.forEach(([queryKey, data]) => {
					queryClient.setQueryData(queryKey, data);
				});
			}

			if (context?.previousUnreadCount) {
				queryClient.setQueryData(
					notificationUnreadCountQueryKeyFactory.all(),
					context.previousUnreadCount
				);
			}

			// Remove from recentlyRead if we added it optimistically
			if (context?.addedToRecentlyRead && context.id) {
				inboxFeatureStore.removeFromRecentlyRead(context.id);
			}
		},
		onSuccess: (data, variables) => {
			if (variables.all === true) {
				invalidateNotificationListQuery();
				queryClient.invalidateQueries({
					queryKey: notificationUnreadCountQueryKeyFactory.all(),
				});
			} else if (!isNil(variables.id)) {
				const shouldInvalidate =
					inboxFeatureStore.showRead ||
					!inboxFeatureStore.isRecentlyRead(variables.id);

				if (shouldInvalidate) {
					invalidateNotificationListQuery();
				}

				const notificationCountQueryData =
					queryClient.getQueryData<NotificationCountData>(
						notificationUnreadCountQueryKeyFactory.all()
					);

				if (!isNil(notificationCountQueryData)) {
					const newCount =
						variables.status === true
							? Math.max(notificationCountQueryData.count - 1, 0)
							: notificationCountQueryData.count + 1;

					queryClient.setQueryData<NotificationCountData>(
						notificationUnreadCountQueryKeyFactory.all(),
						{
							...notificationCountQueryData,
							count: newCount,
						}
					);
				}
			}
		},
	});
}

export function useDeleteAllNotificationsMutation() {
	return useMutation({
		mutationFn: async ({ events, read }: DeleteAllNotificationsParams) => {
			const url = getEndpoints(NOTIFICATION_NAMESPACE).byPath(['delete_all']);
			const { data } = await apiClient.post(
				url,
				{},
				{ params: { events, read } }
			);
			return data;
		},
		onSuccess: () => {
			showNotification({
				title: 'Success',
				message: NOTIFICATION_MESSAGES.DELETE_ALL_SUCCESS,
				color: 'green',
			});
			invalidateNotificationListQuery();
		},
		onError: () => {
			showNotification({
				title: 'Error',
				message: NOTIFICATION_MESSAGES.DELETE_ALL_ERROR,
				color: 'red',
			});
		},
	});
}

export function useRestoreNotificationMutation() {
	return useMutation({
		mutationFn: async ({ notificationId }: { notificationId: string }) => {
			const url = getEndpoints(NOTIFICATION_NAMESPACE).byPath(['restore']);
			const { data } = await apiClient.post(
				url,
				{},
				{ params: { id: notificationId } }
			);
			return data;
		},
		onSuccess: () => {
			showNotification({
				title: 'Success',
				message: NOTIFICATION_MESSAGES.RESTORE_SUCCESS,
				color: 'green',
			});
			invalidateNotificationListQuery();
		},
		onError: () => {
			showNotification({
				title: 'Error',
				message: NOTIFICATION_MESSAGES.RESTORE_ERROR,
				color: 'red',
			});
		},
	});
}

export function useRestoreAllNotificationsMutation() {
	return useMutation({
		mutationFn: async ({
			events,
			read,
			notificationIds,
		}: {
			events?: string;
			read?: boolean;
			notificationIds?: string[];
		}) => {
			const url = getEndpoints(NOTIFICATION_NAMESPACE).byPath(['restore_all']);
			const { data } = await apiClient.post(
				url,
				{ notification_ids: notificationIds },
				{ params: { events, read } }
			);
			return data;
		},
		onSuccess: (data) => {
			showNotification({
				title: 'Success',
				message: `Restored ${data.restored_count} notifications`,
				color: 'green',
			});
			invalidateNotificationListQuery();
		},
		onError: () => {
			showNotification({
				title: 'Error',
				message: NOTIFICATION_MESSAGES.RESTORE_ERROR,
				color: 'red',
			});
		},
	});
}
